
import React, { useEffect, useMemo, useState } from "react";
import {
  BellAlertIcon,
  MagnifyingGlassIcon,
} from "@heroicons/react/24/outline";
import { toast } from "react-toastify";
import ApiCustom from "../../utils/ApiCustom";
import { formatDate } from "../../utils/helps";
import StatusDropdown from "../../components/admin/StatusDropdownButton";
import ConfirmPaidMembershipModal from "../ConfirmPaidMembershipModal";

const PayMembershipFeeModal = ({
  membershipFeeId,
  unpaidOnly = false,
  onOpenReminder,
  onClose,
}) => {
  const [companies, setCompanies] = useState([]);
  const [membershipFee, setMembershipFee] = useState(null);
  const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);
  const [pendingCompany, setPendingCompany] = useState(null);
  const [searchValue, setSearchValue] = useState("");

  useEffect(() => {
    if (membershipFeeId) {
      getMembershipFee();
    }
  }, [membershipFeeId]);

  const getMembershipFee = () => {
    ApiCustom.get(`/membership-fee-shows/${membershipFeeId}`)
      .then((response) => {
        setMembershipFee(response.data?.data?.data);
      })
      .catch((error) => console.log(error));
  };

  useEffect(() => {
    if (membershipFee) {
      const updatedCompanies =
        membershipFee?.companies?.map((company) => ({
          ...company,
          isPaid: company?.CompaniesMustPayMembershipFee?.paid,
        })) || [];

      setCompanies(updatedCompanies);
    }
  }, [membershipFee]);

  const displayedCompanies = useMemo(() => {
    const keyword = searchValue.trim().toLowerCase();

    return companies
      .filter((company) => (unpaidOnly ? !company.isPaid : true))
      .filter((company) => {
        if (!keyword) {
          return true;
        }

        return [
          company.companyName,
          getCompanyRepresentUserName(company),
        ]
          .filter(Boolean)
          .some((value) =>
            String(value).toLowerCase().includes(keyword)
          );
      });
  }, [companies, searchValue, unpaidOnly]);

  const handleStatusChange = async (companyId, newStatus) => {
    const updatedCompanies = companies.map((company) =>
      company.id === companyId
        ? { ...company, isPaid: newStatus }
        : company
    );

    try {
      const updateData = {
        id: companyId,
        isPaid: newStatus,
      };

      await ApiCustom.put(
        `/pay-membership-fee-update/${membershipFee.id}`,
        { paidCompany: updateData },
        {
          headers: {
            "Content-Type": "application/json",
          },
        }
      ).then((response) => {
        if (response.status === 200) {
          setCompanies(updatedCompanies);
          getMembershipFee();
          toast.success("Cập nhật trạng thái thanh toán thành công!");
        }
      });
    } catch (error) {
      toast.error("Đã xảy ra lỗi! Vui lòng thử lại.");
    }
  };

  function getCompanyRepresentUserName(company) {
    const user = company?.users?.find(
      (currentUser) =>
        currentUser?.CompaniesHasUsers?.isUserRepresentOfCompany
    );
    return user ? user?.userFullName : "";
  }

  const handleExportExcel = async () => {
    try {
      const response = await ApiCustom(
        `/pay-membership-fee-export-excel/${membershipFee?.id}`,
        {
          responseType: "blob",
        }
      );
      const blob = response.data;
      const url = window.URL.createObjectURL(blob);
      const anchor = document.createElement("a");
      anchor.href = url;
      anchor.download = "danh_sach_dong_hoi_phi.xlsx";
      document.body.appendChild(anchor);
      anchor.click();
      document.body.removeChild(anchor);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Lỗi khi tải file Excel:", error);
    }
  };

  const handleOpenReminder = () => {
    if (onOpenReminder && membershipFee) {
      onOpenReminder(membershipFee);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-gray-500 bg-opacity-50 p-4">
      <div className="flex max-h-[90vh] w-11/12 flex-col rounded-lg bg-base-100 shadow-xl xl:w-2/3">
        <div className="sticky top-0 z-10 rounded-lg border-b bg-base-100 p-6">
          <div className="flex items-center justify-between">
            <h2 className="text-2xl font-bold">
              {unpaidOnly
                ? "Hội viên chưa đóng hội phí"
                : "Danh sách đóng hội phí"}
            </h2>
            <button
              onClick={onClose}
              className="rounded-md border bg-base-100 px-4 py-2 text-sm font-medium shadow-sm hover:bg-base-200"
            >
              X
            </button>
          </div>

          {unpaidOnly && (
            <div className="relative mt-4 max-w-sm">
              <MagnifyingGlassIcon className="pointer-events-none absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-gray-400" />
              <input
                type="search"
                value={searchValue}
                onChange={(event) => setSearchValue(event.target.value)}
                placeholder="Tìm theo đơn vị hoặc người đại diện..."
                className="input input-bordered w-full pl-10"
              />
            </div>
          )}
        </div>

        {displayedCompanies.length > 0 ? (
          <div className="flex-grow overflow-y-auto overflow-x-auto px-6">
            <table className="my-1 w-full table-auto border-collapse border border-gray-300">
              <thead>
                <tr className="bg-gray-100">
                  <th className="border border-gray-300 px-4 py-2 text-left">
                    Tên đơn vị
                  </th>
                  <th className="border border-gray-300 px-4 py-2 text-left">
                    Người đại diện
                  </th>
                  <th className="border border-gray-300 px-4 py-2 text-left">
                    Ngày cập nhật
                  </th>
                  <th className="border border-gray-300 px-4 py-2 text-center">
                    Trạng thái
                  </th>
                </tr>
              </thead>
              <tbody>
                {displayedCompanies.map((company) => (
                  <tr key={company.id} className="hover:bg-gray-50">
                    <td className="border border-gray-300 px-4 py-2">
                      {company.companyName}
                    </td>
                    <td className="border border-gray-300 px-4 py-2">
                      {getCompanyRepresentUserName(company)}
                    </td>
                    <td className="border border-gray-300 px-4 py-2">
                      {formatDate(
                        company?.CompaniesMustPayMembershipFee?.updatedAt
                      )}
                    </td>
                    <td className="border border-gray-300 px-4 py-2">
                      <div className="flex justify-center">
                        {unpaidOnly ? (
                          <span className="rounded-full bg-yellow-100 px-3 py-1 text-sm text-yellow-800">
                            Chưa đóng
                          </span>
                        ) : (
                          <StatusDropdown
                            disabled={company.isPaid}
                            options={[
                              { value: true, label: "Đã đóng" },
                              { value: false, label: "Chưa đóng" },
                            ]}
                            value={company.isPaid}
                            onChange={(newStatus) => {
                              if (newStatus === true) {
                                setPendingCompany(company);
                                setIsConfirmModalOpen(true);
                              }
                            }}
                            getStatusStyle={(value) =>
                              value
                                ? "bg-green-100 text-green-800"
                                : "bg-yellow-100 text-yellow-800"
                            }
                          />
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="flex min-h-56 items-center justify-center px-6">
            <p className="text-center text-gray-500">
              {unpaidOnly
                ? "Không có hội viên chưa đóng hội phí"
                : "Không có hội viên phải đóng"}
            </p>
          </div>
        )}

        <div className="sticky bottom-0 z-10 rounded-lg border-t bg-base-100 p-6">
          <div className="flex justify-end space-x-2">
            <button
              type="button"
              onClick={onClose}
              className="rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50"
            >
              Đóng
            </button>

            {unpaidOnly ? (
              displayedCompanies.length > 0 && (
                <button
                  type="button"
                  onClick={handleOpenReminder}
                  className="btn btn-primary"
                >
                  <BellAlertIcon className="h-5 w-5" />
                  Gửi thông báo
                </button>
              )
            ) : (
              companies.length > 0 && (
                <button
                  type="button"
                  className="btn btn-outline btn-success"
                  onClick={handleExportExcel}
                >
                  Xuất Excel
                </button>
              )
            )}
          </div>
        </div>
      </div>

      <ConfirmPaidMembershipModal
        isOpen={isConfirmModalOpen}
        onClose={() => {
          setIsConfirmModalOpen(false);
          setPendingCompany(null);
        }}
        onConfirm={() => {
          if (pendingCompany) {
            handleStatusChange(pendingCompany.id, true);
            setIsConfirmModalOpen(false);
            setPendingCompany(null);
          }
        }}
      />
    </div>
  );
};

export default PayMembershipFeeModal;

